/**
 * Settings panel — the right-docked, resizable, **non-modal** drawer that
 * edits the operator's **live** UI configuration and snapshots it into
 * Profiles (ADR-033 §5; REQ-UI-076 / 078 / 080 / 086 / 087 / 088 / 092 / 094).
 *
 * # The new model (architect 2026-06-08, part 1)
 *
 * Settings are **always live**. There is no draft, no preview overlay, no
 * Cancel: every section control reads ``usePreferences().workingState.<slice>``
 * and writes through :func:`setWorkingState`, so each edit applies immediately
 * and persists to preferences (it survives reload). A change is an "unsaved
 * override" to the active Profile — the working state diverges from it (the
 * derived ``(modified)`` hash; REQ-UI-074). The only "undo" is re-applying a
 * Profile (Apply / a later "Reset to profile"), not built here.
 *
 * Saving snapshots the **current working state — including the live panel
 * layout and the live map viewport** — into a Profile, either as a new one or
 * as an update to the active one:
 *
 *   - **Save as new Profile** prompts for a name (required, unique within the
 *     chosen scope) + a scope (User / Shared; Shared admin-gated), then
 *     ``createProfile`` with a document = the working state + the live layout
 *     (:func:`MultiPaneApi.captureLayout`) + the live viewport (from
 *     ``map-viewport-store``, written into ``map.home``). On success the saved
 *     Profile becomes active.
 *   - **Update current Profile** writes the same current-working-state-incl-
 *     layout-and-viewport into the **active** Profile via the shared
 *     :func:`useSaveProfile().updateProfileFromLive` (the SAME path the gear-menu
 *     pencil uses) — a full-document diff, so a layout-only or viewport-only
 *     change is persisted and "Nothing to update" shows only on a true match.
 *     Disabled when there is no active Profile, or the user can't write it
 *     (non-admin + Shared scope).
 *
 * # Non-modal + live behind
 *
 * The panel is a labelled ``<aside aria-label="Settings">`` fixed to the right
 * edge — **not** a Radix modal and **not** a dockview panel. The canvas stays
 * live behind it (the operator watches the map update while editing), which is
 * why §5.1 rejects a blocking modal. A drag handle on the left edge resizes it
 * (REQ-UI-076 — "resizable"); width is local UI state, clamped to a sane range.
 *
 * # Collapsible sections
 *
 * Every section group (Units / Clocks / Map / Entity Details) is a
 * :func:`ProfileEditorSection` — the same chevron + uppercase-title disclosure
 * the live Entity Details panel uses — and **all start expanded** (2026-06-12) so
 * every setting is visible at a glance; the operator collapses any they don't
 * need. There is no Layout section:
 * the live panel layout is captured at save time from the dockview workspace
 * (:func:`MultiPaneApi.captureLayout`), which owns arranging, so there is nothing
 * to surface or arrange here (the read-only "N panels" summary was removed).
 *
 * # Update-active gating only (REQ-UI-078 / 079)
 *
 * The panel always edits the live working state (the operator's environment).
 * The only read-only treatment is the **Update current Profile** button: it is
 * disabled when the active Profile isn't writable by the caller (a non-admin
 * facing a Shared active Profile). Save-as-new is always available.
 *
 * Cross-references:
 *   - REQ-UI-076 (drawer; resizable; live edit).
 *   - REQ-UI-078 (save scope + admin gate on Shared).
 *   - REQ-UI-074 (working-state ↔ active-Profile divergence indicator).
 *   - REQ-UI-086 / 087 / 088 (map marker colour + glyph), REQ-UI-092 (entity
 *     detail defaults).
 *   - REQ-UI-094 (labelled aside, tab order, disabled-state semantics).
 *   - ADR-033 §5; ADR-009 §5 (admin authority).
 *   - profiles/profile-editor-store.ts (open/close); multi-pane captureLayout;
 *     map/map-viewport-store (live viewport).
 */
import { Trash } from "@phosphor-icons/react";
import { useCallback, useRef, useState } from "react";
import { useTileOptions } from "@/chrome/use-deployment-config";
import type {
  ClockSettings,
  CoordinateFormat,
  DistanceUnit,
  MapSettings,
  ProfileDocument,
} from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";
import { cn } from "@/lib/utils";
import { usePreferences } from "@/preferences/preferences-context";
import { ProfileEditorSection } from "@/profiles/profile-editor-section";
import {
  ALERT_PRIORITY,
  ALERT_PRIORITY_OVERRIDE_KEY,
  ALERT_TYPE,
  ALERT_TYPE_OVERRIDE_KEY,
  getAlertPriorityColor,
  getAlertTypeColor,
  getStatusColor,
  type MarkerStatus,
  type OverrideColors,
} from "@/map/marker-styling";
import { GLYPH_IDS, GLYPH_LABELS, normalizeGlyphId, type GlyphId } from "@/map/glyphs";
import {
  DEFAULT_SELECTED_DASH_PATTERN,
  SELECTED_DASH_PATTERNS,
  normalizeSelectedDashPattern,
  type SelectedDashPattern,
} from "@/map/selected-dash-pattern";
import { GlyphIcon } from "@/map/glyph-icon";
import {
  DYNAMIC_TRACK_DEFAULTS,
  DYNAMIC_TRACK_MAX_KM,
  DYNAMIC_TRACK_MIN_KM,
  DYNAMIC_TRACK_STEP_KM,
} from "@/map/use-dynamic-track";
import { NumberStepper } from "@/map/number-stepper";
import { alertTypeLabel, describeAlertPriority } from "@/alerts/alert-display";
import {
  SECTION_IDS,
  SECTION_TITLES,
  type EntityDetailType,
} from "@/entity-details/use-section-visibility";
import { useProfileEditorActions, useSettingsPanelOpen } from "@/profiles/profile-editor-store";

/** Settings drawer width bounds (px). The default opens at the minimum — as
 *  narrow as the controls allow (operator request 2026-06-12) — so the panel
 *  takes the least space by default; the operator can widen it (the left-edge
 *  drag handle) for the Map/Entity sections. */
const MIN_WIDTH = 320;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 320;

/** Maximum number of clock rows (ADR-033 §8 — 0..4). */
const MAX_CLOCKS = 4;
/** Maximum clock-label length (ADR-033 §8). */
const CLOCK_LABEL_MAX = 8;

/**
 * The mount point for the Settings panel. Renders nothing when the panel is
 * closed; otherwise renders the drawer. Mounted once in :class:`AppShell` so it
 * overlays every authenticated route.
 */
export function ProfileEditor(): React.ReactElement | null {
  const isOpen = useSettingsPanelOpen();
  if (!isOpen) {
    return null;
  }
  return <SettingsDrawer />;
}

/**
 * The drawer body — edits the live working state in place and owns the
 * save-as-new / update-active machinery + the resize width.
 *
 * Every section control reads ``workingState.<slice>`` and writes the whole
 * next slice via :func:`patch` (a thin ``setWorkingState`` wrapper), so a
 * single control never drops a sibling field (REQ-AUTH-060 round-trip
 * discipline). Because the rest of the app already renders off the live
 * working state, the canvas tracks every edit with zero consumer changes.
 */
function SettingsDrawer(): React.ReactElement {
  const { workingState, setWorkingState } = usePreferences();
  const { close } = useProfileEditorActions();

  // Live-edit error (a failed ``setWorkingState`` PUT rolls back + surfaces
  // here). The Profile save/update controls + the "Unsaved changes" indicator
  // moved entirely to the gear → Profiles submenu (operator request 2026-06-12,
  // removing the redundant footer); the active-Profile "(modified)" status stays
  // visible in the top bar (REQ-UI-074).
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);

  // ---- Live working-state edit -------------------------------------
  // Each section control hands us the whole next top-level slice (e.g.
  // ``{ theme }``, ``{ units }``, ``{ map }``); we forward it to the optimistic
  // ``setWorkingState`` which round-trips the rest of the document. Errors
  // surface inline (the PUT rolled back).
  const patch = useCallback(
    (next: Partial<ProfileDocument>): void => {
      setError(null);
      void setWorkingState(next).then((result) => {
        if (result.status === "error") {
          setError(result.message);
        }
      });
    },
    [setWorkingState],
  );

  // ---- Resize handle ------------------------------------------------
  // Pointer-driven left-edge drag; width grows as the pointer moves left.
  const widthRef = useRef(width);
  widthRef.current = width;
  const onResizePointerDown = useCallback((event: React.PointerEvent): void => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = widthRef.current;
    const onMove = (e: PointerEvent): void => {
      const delta = startX - e.clientX; // drag left ⇒ wider
      setWidth(clampWidth(startWidth + delta));
    };
    const onUp = (): void => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, []);

  return (
    <aside
      aria-label="Settings"
      data-testid="profile-editor"
      className={cn(
        "fixed bottom-0 right-0 top-0 z-40 flex flex-col",
        // Match the entity-detail panels' surface family: the panels render on
        // ``bg-bg-base`` (the darkest token; entity-details-panel.tsx) — NOT the
        // lightest ``bg-bg-overlay`` modal/popover token — so the drawer reads
        // as the same operator surface, not a floating overlay (item 7).
        "border-l border-border bg-bg-base shadow-xl",
      )}
      style={{ width }}
    >
      {/* Left-edge resize handle (REQ-UI-076 — resizable). */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize Settings panel"
        data-testid="profile-editor-resize"
        onPointerDown={onResizePointerDown}
        className={cn("absolute left-0 top-0 h-full w-1.5 cursor-col-resize", "hover:bg-brand/30")}
      />

      {/* Header */}
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-body font-medium text-text-primary" data-testid="profile-editor-title">
          Settings
        </h2>
        <button
          type="button"
          onClick={close}
          aria-label="Close Settings"
          data-testid="profile-editor-close"
          className="rounded p-1 text-text-secondary hover:bg-bg-elevated hover:text-text-primary"
        >
          ✕
        </button>
      </header>

      {/* Body — scrollable form editing the live working state. The sections
       * render flush + divider-separated (matching the Entity Details panel's
       * bare ``overflow-y-auto`` container): no body padding and no inter-
       * section gap — each ProfileEditorSection owns its own px-4 header/body
       * padding + ``border-b`` divider (2026-06-09 look-and-feel alignment). */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col">
          <UnitsSection
            distance={workingState.units?.distance ?? "nautical_miles"}
            coordinateFormat={workingState.units?.coordinate_format ?? "decimal_degrees"}
            onChange={(next) =>
              patch({
                units: { distance: next.distance, coordinate_format: next.coordinate_format },
              })
            }
          />
          <ClocksSection
            clocks={workingState.clocks ?? []}
            onChange={(clocks) => patch({ clocks })}
          />
          <MapSection map={workingState.map} onChange={(map) => patch({ map })} />
          <EntityDetailsSection
            entityDetails={workingState.entity_details}
            onChange={(entity_details) => patch({ entity_details })}
          />
        </div>
      </div>

      {/* Footer — live-edit error only. The Profile save/update controls + the
       * "Unsaved changes" indicator were removed (operator request 2026-06-12;
       * redundant with the gear → Profiles submenu). Renders nothing unless a
       * live edit's PUT failed, so the drawer ends flush with the last section
       * — no empty footer bar. The active-Profile "(modified)" status stays
       * visible in the top bar (REQ-UI-074). */}
      {error !== null && (
        <footer className="border-t border-border px-4 py-3">
          <p role="alert" data-testid="profile-editor-error" className="text-meta text-destructive">
            {error}
          </p>
        </footer>
      )}
    </aside>
  );
}

// ════════════════════════════════════════════════════════════════════
// Scalar settings sections
// ════════════════════════════════════════════════════════════════════

/** Units section — distance + coordinate format (live reformat). */
function UnitsSection({
  distance,
  coordinateFormat,
  onChange,
}: {
  distance: DistanceUnit;
  coordinateFormat: CoordinateFormat;
  onChange: (next: { distance: DistanceUnit; coordinate_format: CoordinateFormat }) => void;
}): React.ReactElement {
  return (
    <ProfileEditorSection title="Units" testId="units">
      <label className="flex flex-col gap-1">
        <span className="text-body-sm text-text-secondary">Distance</span>
        <select
          value={distance}
          onChange={(e) =>
            onChange({
              distance: e.target.value as DistanceUnit,
              coordinate_format: coordinateFormat,
            })
          }
          data-testid="profile-editor-units-distance"
          className={selectClass}
        >
          <option value="nautical_miles">Nautical miles</option>
          <option value="kilometers">Kilometers</option>
          <option value="statute_miles">Statute miles</option>
        </select>
      </label>
      <label className="mt-2 flex flex-col gap-1">
        <span className="text-body-sm text-text-secondary">Coordinate format</span>
        <select
          value={coordinateFormat}
          onChange={(e) =>
            onChange({ distance, coordinate_format: e.target.value as CoordinateFormat })
          }
          data-testid="profile-editor-units-coordinate"
          className={selectClass}
        >
          <option value="decimal_degrees">Decimal degrees</option>
          <option value="dms">Degrees / minutes / seconds</option>
          <option value="mgrs">MGRS</option>
        </select>
      </label>
    </ProfileEditorSection>
  );
}

/**
 * Clocks section — 0..4 rows, each label (≤8 chars) / timezone / hour cycle /
 * seconds. Applies live in the top bar (the clocks read ``workingState.clocks``;
 * ADR-033 §8). Notable behaviours:
 *
 *   - **Constrained timezone picker** — a native ``<select>`` over the runtime's
 *     full IANA zone list ({@link supportedTimezones}). A native select gives
 *     type-ahead but cannot hold an off-list value, so an invalid free-text zone
 *     can never reach ``Intl.DateTimeFormat``. The select is keyboard-operable
 *     by default (REQ-UI-094).
 *   - **Label auto-fill** — a new row's label, and a row whose label is still
 *     "auto" (empty, or equal to the prior zone's derived city), follows the
 *     timezone via {@link clockLabelFromTimezone}. A user-typed label is left
 *     alone.
 *   - **Unique labels** — labels (not timezones) must be unique case-insensitively.
 *     Duplicate rows show an inline message + a destructive border; Add is
 *     disabled while a duplicate is unresolved or a new default row would collide.
 *   - **Time only** — no date control; ``show_date`` is an unused POST-MVP hook
 *     and the top bar renders time only.
 */
function ClocksSection({
  clocks,
  onChange,
}: {
  clocks: readonly ClockSettings[];
  onChange: (clocks: ClockSettings[]) => void;
}): React.ReactElement {
  const tzOptions = supportedTimezones();
  const atCap = clocks.length >= MAX_CLOCKS;
  const dupes = duplicateClockLabelIndices(clocks);
  const addBlocked = atCap || dupes.size > 0;

  const update = (index: number, patch: Partial<ClockSettings>): void => {
    onChange(clocks.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };
  const updateTimezone = (index: number, timezone: string): void => {
    // Defence in depth: only ever commit a value that is on the closed list.
    if (!tzOptions.includes(timezone)) {
      return;
    }
    const current = clocks[index];
    const currentLabel = (current?.label ?? "").trim();
    const wasAuto =
      currentLabel === "" || currentLabel === clockLabelFromTimezone(current?.timezone ?? "");
    if (!wasAuto) {
      update(index, { timezone });
      return;
    }
    const others = clocks.filter((_, i) => i !== index).map((c) => c.label ?? "");
    update(index, { timezone, label: uniqueClockLabel(clockLabelFromTimezone(timezone), others) });
  };
  const add = (): void => {
    if (addBlocked) {
      return;
    }
    const base = defaultClock();
    const label = uniqueClockLabel(
      clockLabelFromTimezone(base.timezone),
      clocks.map((c) => c.label ?? ""),
    );
    onChange([...clocks, { ...base, label }]);
  };
  const remove = (index: number): void => {
    onChange(clocks.filter((_, i) => i !== index));
  };

  const addDisabledReason = atCap
    ? "Maximum of 4 clocks"
    : dupes.size > 0
      ? "Resolve duplicate clock labels first"
      : undefined;

  return (
    <ProfileEditorSection title="Clocks" testId="clocks">
      {clocks.length === 0 && (
        <p className="text-meta text-text-muted" data-testid="profile-editor-clocks-empty">
          No clocks. The top bar shows none until you add one.
        </p>
      )}
      <div className="flex flex-col gap-3">
        {clocks.map((clock, index) => {
          const isDuplicate = dupes.has(index);
          return (
            <div
              key={index}
              data-testid={`profile-editor-clock-${index}`}
              className="rounded border border-border bg-bg-elevated p-2"
            >
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={clock.label ?? ""}
                  maxLength={CLOCK_LABEL_MAX}
                  placeholder="Label"
                  onChange={(e) => update(index, { label: e.target.value })}
                  data-testid={`profile-editor-clock-${index}-label`}
                  aria-label={`Clock ${index + 1} label`}
                  aria-invalid={isDuplicate || undefined}
                  className={cn("w-24", inputClass, isDuplicate && "border-destructive")}
                />
                {/* Constrained picker: a native <select> over the full IANA list
                 * — type-ahead works, off-list values are impossible. */}
                <select
                  value={clock.timezone}
                  onChange={(e) => updateTimezone(index, e.target.value)}
                  data-testid={`profile-editor-clock-${index}-timezone`}
                  aria-label={`Clock ${index + 1} timezone`}
                  className={cn("min-w-0 flex-1", selectClass)}
                >
                  {/* Guard against a persisted zone outside the runtime list. */}
                  {!tzOptions.includes(clock.timezone) && (
                    <option value={clock.timezone}>{clock.timezone}</option>
                  )}
                  {tzOptions.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => remove(index)}
                  data-testid={`profile-editor-clock-${index}-remove`}
                  aria-label={`Remove clock ${index + 1}`}
                  title="Remove clock"
                  className={cn(
                    "shrink-0 rounded border border-border p-1.5 text-text-secondary",
                    "hover:bg-bg-elevated hover:text-text-primary",
                  )}
                >
                  {/* Trash is the intended affordance (restored 2026-06-08,
                   * part 2). The button keeps its ``aria-label`` so the icon is
                   * decorative (REQ-UI-094). */}
                  <Trash size={14} aria-hidden />
                </button>
              </div>
              {isDuplicate && (
                <p
                  role="alert"
                  data-testid={`profile-editor-clock-${index}-dup-label`}
                  className="mt-1 text-meta text-destructive"
                >
                  Clock labels must be unique.
                </p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-3 text-body-sm text-text-secondary">
                <label className="flex items-center gap-1">
                  <span>24-hour</span>
                  <input
                    type="checkbox"
                    checked={clock.hour_cycle === "h23"}
                    onChange={(e) =>
                      update(index, { hour_cycle: e.target.checked ? "h23" : "h12" })
                    }
                    data-testid={`profile-editor-clock-${index}-hour-cycle`}
                  />
                </label>
                <label className="flex items-center gap-1">
                  <span>Seconds</span>
                  <input
                    type="checkbox"
                    checked={clock.show_seconds}
                    onChange={(e) => update(index, { show_seconds: e.target.checked })}
                    data-testid={`profile-editor-clock-${index}-show-seconds`}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        disabled={addBlocked}
        onClick={add}
        data-testid="profile-editor-clock-add"
        title={addDisabledReason}
        className={cn(
          "mt-2 self-start rounded border border-border px-3 py-1.5 text-body-sm",
          "text-text-primary hover:bg-bg-elevated",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        New Clock
      </button>
    </ProfileEditorSection>
  );
}

// ════════════════════════════════════════════════════════════════════
// Map editor section (REQ-UI-086 / 087 / 088)
// ════════════════════════════════════════════════════════════════════

// The Profile ``map`` sub-document is :type:`MapSettings` (output variant;
// ADR-033 §9). The nested-slice aliases below bind the local control props to
// the generated wire shapes.
/** Platform marker glyph + colour (ADR-033 §9.2). */
type PlatformMapSettings = components["schemas"]["PlatformMapSettings"];
/** Detection glyph + colour (ADR-033 §9). */
type DetectionMapSettings = components["schemas"]["DetectionMapSettings"];
/** Alert glyph + colour mode (ADR-033 §9.7). */
type AlertMapSettings = components["schemas"]["AlertMapSettings"];
/** Toggleable-layer default visibility (ADR-033 §9.6). */
type VisibleLayers = components["schemas"]["VisibleLayers"];
type TrackMapSettings = components["schemas"]["TrackMapSettings"];

// NOTE: ``map.platform.geometry_defaults`` (the per-sensor §9.3 field) is a
// vestigial schema hook — geometry-layer visibility is driven by
// ``map.visible_layers.{fov,coverage,obscuration}`` (the Layer Visibility
// controls, read by ``use-geometry-layers``). The redundant + non-functional
// "Geometry defaults" sub-control was removed from this panel; the schema field
// is left untouched (never wired) so the type/const for it are intentionally
// not declared here.

/** Schema-default platform colour slice (ADR-033 §9.2). */
const DEFAULT_PLATFORM: PlatformMapSettings = {
  color_mode: "by_status",
  single_color: "#3ecf8e",
  glyph: "triangle",
};
/** Schema-default detection slice (ADR-033 §9). */
const DEFAULT_DETECTION: DetectionMapSettings = {
  color_mode: "single",
  glyph: "circle",
  single_color: "#22d3ee",
};
/** Schema-default alert slice (ADR-033 §9.7). */
const DEFAULT_ALERT: AlertMapSettings = {
  color_mode: "by_alert_type",
  glyph: "diamond",
  single_color: "#e879f9",
};
/** Schema-default object-track slice (ADR-041 D-4; REQ-UI-200). */
const DEFAULT_TRACK: TrackMapSettings = {
  selected_dash: true,
  selected_dash_pattern: DEFAULT_SELECTED_DASH_PATTERN,
};

/**
 * The overlay patterns, in the schema's order (finest first), with the labels
 * the Settings drawer shows. One vocabulary with the layer's
 * :data:`SELECTED_DASH_PATTERNS` by construction — the keys are typed by it.
 */
const SELECTED_DASH_PATTERN_LABELS: Readonly<Record<SelectedDashPattern, string>> = {
  dotted: "Dotted",
  short_dash: "Short dashes",
  long_dash: "Long dashes",
};
/** Schema-default toggleable-layer visibility (ADR-033 §9.6). */
const DEFAULT_VISIBLE_LAYERS: VisibleLayers = {
  trails: true,
  detections: true,
  alerts: true,
  tracks: true,
  fov: false,
  coverage: false,
  obscuration: false,
};

/**
 * The Map editor section (ADR-033 §9; REQ-UI-086 / 087 / 088).
 *
 * A collapsible group whose controls each compute the **whole** next ``map``
 * sub-document (reading the current slice, spreading their change in) and hand
 * it to ``onChange`` — wired to ``setWorkingState({ map })`` so the edit lands
 * in the working state AND applies live on the canvas (the Map reads
 * ``workingState.map.*``). A single control never drops a sibling field.
 *
 * Part 1 removed the **Home viewport** controls ("Set home to current view" /
 * "Preview home"): the map camera is captured into ``map.home`` at save time
 * from the live viewport store, so there is no separate home control here.
 *
 * The **Tile server & basemap** picker (:func:`TileServerPicker`) writes
 * ``map.tile_server`` (REQ-UI-091; #366) — the advertised option id, or
 * ``null`` for the deployment default. ``map.basemap`` stays an unconsumed
 * POST-MVP hook (a single id picks the source today; ADR-033 §2).
 *
 * @param map - The current ``workingState.map`` (possibly ``undefined`` — the
 *     controls then edit from schema defaults).
 * @param onChange - Receives the whole next ``map`` sub-document.
 */
function MapSection({
  map,
  onChange,
}: {
  map: MapSettings | undefined;
  onChange: (map: MapSettings) => void;
}): React.ReactElement {
  const platform: PlatformMapSettings = { ...DEFAULT_PLATFORM, ...map?.platform };
  const detection: DetectionMapSettings = { ...DEFAULT_DETECTION, ...map?.detection };
  const alert: AlertMapSettings = { ...DEFAULT_ALERT, ...map?.alert };
  const visibleLayers: VisibleLayers = { ...DEFAULT_VISIBLE_LAYERS, ...map?.visible_layers };
  const track: TrackMapSettings = { ...DEFAULT_TRACK, ...map?.track };

  // Whole-``map`` patchers — each reads the current ``map`` (or {}) and spreads
  // the one changed nested slice, so unrelated slices (home, the STUBBED
  // tile_server/basemap) round-trip untouched (REQ-AUTH-060).
  const patchMap = (patch: Partial<MapSettings>): void => {
    onChange({ ...map, ...patch });
  };
  const patchPlatform = (patch: Partial<PlatformMapSettings>): void => {
    patchMap({ platform: { ...platform, ...patch } });
  };

  return (
    <ProfileEditorSection title="Map" testId="map">
      <TileServerPicker
        value={map?.tile_server ?? null}
        onChange={(tile_server) => patchMap({ tile_server })}
      />

      <LayerVisibilityControls
        layers={visibleLayers}
        onChange={(visible_layers) => patchMap({ visible_layers })}
      />

      <PlatformControls
        platform={platform}
        onChange={(next) => patchMap({ platform: next })}
        onPatch={patchPlatform}
      />

      <DetectionControls detection={detection} onChange={(next) => patchMap({ detection: next })} />

      <AlertControls alert={alert} onChange={(next) => patchMap({ alert: next })} />

      <TrackControls track={track} onChange={(next) => patchMap({ track: next })} />
    </ProfileEditorSection>
  );
}

/**
 * Tile server & basemap picker (REQ-UI-091; #366; ADR-016 amended 2026-06-10).
 *
 * A native ``<select>`` over the deployment-advertised basemap sources
 * (``GET /api/config`` → ``map.tile_options[]``, read via :func:`useTileOptions`).
 * There is **one row per advertised option** — no synthetic "use deployment
 * default" row. Instead, when the Profile's ``map.tile_server`` is unset/null,
 * the control *displays* the deployment-default option (the one flagged
 * ``default: true``) as pre-selected, while **writing nothing on mount** — the
 * resolution of unset → default is purely presentational, so an untouched
 * Profile's ``tile_server`` stays unset and the document does not look
 * "(modified)" (the content hash drops the absent optional field — see
 * :file:`profile-hash.ts`). The chosen option's ``id`` writes to
 * ``map.tile_server``.
 *
 * Why the option *id* (not a URL): at MVP only an advertised id is meaningful —
 * the Map seam (:func:`selectTileDescriptor`) resolves the id back to a
 * descriptor and falls back to the deployment default for an unset/stale id
 * (storyboard U-profiles-and-settings edge-case table). The picker mirrors that
 * fallback for **display** via ``value ?? defaultId``.
 *
 * Edge cases preserved from the prior sentinel design:
 *   - **Stale id** — a persisted ``tile_server`` the deployment no longer
 *     advertises still shows (an extra "(unavailable)" row) and stays selected,
 *     rather than silently snapping to the default; the Map seam renders the
 *     default for it.
 *   - **No advertised options** — never render a blank control (CLAUDE.md §8.1);
 *     show a single disabled placeholder row indicating the deployment default
 *     is in effect.
 *
 * The select is keyboard-operable + labelled by default (the ``aria-label`` is
 * preserved from the prior stub; REQ-UI-094).
 *
 * @param value - The current ``map.tile_server`` id (``null`` = unset; the
 *     control displays the deployment-default option but writes nothing).
 * @param onChange - Receives the chosen option id (a real advertised id).
 */
function TileServerPicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (tileServer: string | null) => void;
}): React.ReactElement {
  const options = useTileOptions();
  // The deployment-default option id — what an unset ``tile_server`` resolves
  // to for DISPLAY (mirrors :func:`selectTileDescriptor`'s render-side
  // fallback). Keyed off the ``default: true`` descriptor, not a hardcoded id.
  const defaultId = options.find((o) => o.default)?.id;
  // Resolve unset/null → the default option for display only (no write).
  const displayedValue = value ?? defaultId ?? "";
  // A persisted id this deployment no longer advertises must still be
  // selectable in the control (so the value round-trips + shows), even though
  // the Map seam falls back to the default for rendering. Surface it as an
  // extra "(unavailable)" row rather than silently snapping to the default.
  const selectedIsStale = value !== null && value !== "" && !options.some((o) => o.id === value);

  // Empty-options fallback: never render a blank control (CLAUDE.md §8.1).
  // Show one disabled placeholder row indicating the deployment default is in
  // effect — one effectively-fixed choice, not an empty/blank select.
  if (options.length === 0) {
    return (
      <SubGroup label="Tile server & basemap">
        <select
          value=""
          disabled
          data-testid="profile-editor-map-tile"
          aria-label="Tile server and basemap"
          className={selectClass}
        >
          <option value="">Deployment default</option>
        </select>
      </SubGroup>
    );
  }

  return (
    <SubGroup label="Tile server & basemap">
      <select
        value={displayedValue}
        onChange={(e) => onChange(e.target.value)}
        data-testid="profile-editor-map-tile"
        aria-label="Tile server and basemap"
        className={selectClass}
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
        {selectedIsStale && (
          // A stale Profile id — keep it shown so the value persists + stays
          // selected; the Map renders the deployment default for it
          // (selectTileDescriptor).
          <option value={value as string}>{value} (unavailable)</option>
        )}
      </select>
    </SubGroup>
  );
}

/**
 * Toggleable-layer visibility defaults (ADR-033 §9.6). Seven checkboxes (trails /
 * detections / alerts / tracks / fov / coverage / obscuration) writing
 * ``map.visible_layers``. Platform clusters/markers are always-on (not listed),
 * matching the BFF ``VisibleLayers`` shape.
 */
function LayerVisibilityControls({
  layers,
  onChange,
}: {
  layers: VisibleLayers;
  onChange: (layers: VisibleLayers) => void;
}): React.ReactElement {
  const rows: { key: keyof VisibleLayers; label: string }[] = [
    { key: "trails", label: "Trails" },
    { key: "detections", label: "Detections" },
    { key: "alerts", label: "Alerts" },
    // ADR-041 (REQ-UI-173): the object-track polylines; off hides the lines
    // and leaves the head glyphs.
    { key: "tracks", label: "Tracks" },
    { key: "fov", label: "Field of view" },
    { key: "coverage", label: "Coverage" },
    { key: "obscuration", label: "Obscuration" },
  ];
  return (
    <SubGroup label="Layer visibility">
      <div className="flex flex-col gap-1.5">
        {rows.map((row) => (
          <CheckboxRow
            key={row.key}
            label={row.label}
            checked={layers[row.key]}
            testId={`profile-editor-map-layer-${row.key}`}
            onChange={(checked) => onChange({ ...layers, [row.key]: checked })}
          />
        ))}
      </div>
    </SubGroup>
  );
}

/**
 * Object-track line controls (ADR-041 D-4; REQ-UI-167 / -200).
 *
 * A toggle — whether the **selected** track carries the patterned overlay —
 * and a radio group for which pattern it carries (REQ-UI-200 amended
 * 2026-10-19). Not offered here, because it is not optional: the selected
 * line's extra width, which REQ-UI-167 requires — an operator able to switch
 * it off would be switching off the selection indicator itself. The selected
 * line's colour is not a setting either, in the other direction: it is the
 * track colour (``map.detection.single_color``) and nothing else, because a
 * selection hue the operator could also pick for every track would be no
 * emphasis at all. The overlay is the judgement call, and it belongs to the
 * deployment: a fine dotted line marks a selection on a sparse theatre
 * without reading as a second track, a longer dash separates two parallel
 * tracks on a dense one.
 */
function TrackControls({
  track,
  onChange,
}: {
  track: TrackMapSettings;
  onChange: (track: TrackMapSettings) => void;
}): React.ReactElement {
  const pattern = normalizeSelectedDashPattern(track.selected_dash_pattern);
  return (
    <SubGroup label="Tracks">
      <CheckboxRow
        label="Mark the selected track with a pattern"
        checked={track.selected_dash}
        testId="profile-editor-map-track-selected-dash"
        onChange={(checked) => onChange({ ...track, selected_dash: checked })}
      />
      <div className="mt-2">
        <span
          id="profile-editor-map-track-dash-pattern-label"
          className="text-body-sm font-medium text-text-secondary"
        >
          Pattern
        </span>
        <div
          role="radiogroup"
          aria-labelledby="profile-editor-map-track-dash-pattern-label"
          data-testid="profile-editor-map-track-dash-pattern-group"
          className="mt-1"
        >
          <ColorModeRadio
            name="profile-editor-map-track-dash-pattern"
            value={pattern}
            options={SELECTED_DASH_PATTERNS.map((id) => ({
              value: id,
              label: SELECTED_DASH_PATTERN_LABELS[id],
            }))}
            testIdPrefix="profile-editor-map-track-dash-pattern"
            onChange={(value) =>
              onChange({ ...track, selected_dash_pattern: normalizeSelectedDashPattern(value) })
            }
          />
        </div>
      </div>
    </SubGroup>
  );
}

/**
 * Platform marker controls (ADR-033 §9.2; REQ-UI-086).
 *
 * Colour mode (single / by-status) + a single-colour swatch (shown in
 * ``single`` mode) + a **By-Status legend** of editable per-status colour
 * pickers (shown in ``by_status`` mode; inc-items 4/5, REQ-UI-086 — symmetric
 * with the alert legends); dynamic-track enabled + length stepper (bounds reused
 * from :file:`map/use-dynamic-track.ts`); a glyph picker writing
 * ``map.platform.glyph`` (REQ-UI-086).
 *
 * Geometry-layer visibility is **not** edited here — it is driven by the Layer
 * Visibility controls (``map.visible_layers.{fov,coverage,obscuration}``, read
 * by ``use-geometry-layers``). The old per-platform "Geometry defaults"
 * sub-control (``map.platform.geometry_defaults``) was redundant + never wired,
 * so it was removed; that schema field is now a vestigial hook.
 *
 * @param onChange - Replace the whole ``platform`` slice (used by dynamic-track,
 *     which builds a fresh sub-object).
 * @param onPatch - Patch one ``platform`` field (used by colour mode + swatch +
 *     glyph picker).
 */
function PlatformControls({
  platform,
  onChange,
  onPatch,
}: {
  platform: PlatformMapSettings;
  onChange: (platform: PlatformMapSettings) => void;
  onPatch: (patch: Partial<PlatformMapSettings>) => void;
}): React.ReactElement {
  const track = {
    enabled: platform.dynamic_track?.enabled ?? DYNAMIC_TRACK_DEFAULTS.enabled,
    length_km: platform.dynamic_track?.length_km ?? DYNAMIC_TRACK_DEFAULTS.length_km,
  };
  return (
    <SubGroup label="Platform">
      <ColorGroup label="Marker colour" headerId="profile-editor-platform-color-label" flush>
        <ColorModeRadio
          name="profile-editor-platform-color-mode"
          value={platform.color_mode}
          options={[
            { value: "by_status", label: "By status" },
            { value: "single", label: "Single colour" },
          ]}
          onChange={(mode) => onPatch({ color_mode: mode as PlatformMapSettings["color_mode"] })}
          testIdPrefix="profile-editor-platform-color"
        />
        {platform.color_mode === "single" && (
          <ColorSwatch
            label="Marker colour"
            hideLabel
            value={platform.single_color}
            testId="profile-editor-platform-single-color"
            onChange={(single_color) => onPatch({ single_color })}
          />
        )}
        {platform.color_mode === "by_status" && (
          <PlatformStatusLegend
            overrides={platform.status_colors}
            onChange={(status_colors) => onPatch({ status_colors })}
          />
        )}
      </ColorGroup>

      <div className="mt-3">
        <CheckboxRow
          label="Platform trails"
          checked={track.enabled}
          testId="profile-editor-platform-track-enabled"
          onChange={(enabled) => onChange({ ...platform, dynamic_track: { ...track, enabled } })}
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <label
            htmlFor="profile-editor-platform-track-km"
            className="text-body-sm text-text-secondary"
          >
            Tail length
          </label>
          <NumberStepper
            id="profile-editor-platform-track-km"
            ariaLabel="Platform trail length in kilometres"
            unitLabel="km"
            value={track.length_km}
            min={DYNAMIC_TRACK_MIN_KM}
            max={DYNAMIC_TRACK_MAX_KM}
            step={DYNAMIC_TRACK_STEP_KM}
            onChange={(length_km) =>
              onChange({ ...platform, dynamic_track: { ...track, length_km } })
            }
          />
        </div>
      </div>

      <GlyphPicker
        label="Marker glyph"
        value={platform.glyph}
        testIdPrefix="profile-editor-platform-glyph"
        onChange={(glyph) => onPatch({ glyph })}
      />
    </SubGroup>
  );
}

/**
 * Detection marker controls (ADR-033 §9; REQ-UI-087). A single-colour swatch
 * for ``map.detection.single_color`` (the only colour mode is the const
 * ``"single"``, so there's no mode selector) + a glyph picker writing
 * ``map.detection.glyph`` (REQ-UI-087).
 */
function DetectionControls({
  detection,
  onChange,
}: {
  detection: DetectionMapSettings;
  onChange: (detection: DetectionMapSettings) => void;
}): React.ReactElement {
  return (
    <SubGroup label="Detection">
      <ColorGroup label="Marker colour" headerId="profile-editor-detection-color-label" flush>
        <ColorSwatch
          label="Marker colour"
          hideLabel
          value={detection.single_color}
          testId="profile-editor-detection-single-color"
          onChange={(single_color) => onChange({ ...detection, single_color })}
        />
      </ColorGroup>
      <GlyphPicker
        label="Glyph"
        value={detection.glyph}
        testIdPrefix="profile-editor-detection-glyph"
        onChange={(glyph) => onChange({ ...detection, glyph })}
      />
    </SubGroup>
  );
}

/**
 * Alert glyph controls (ADR-033 §9.7; REQ-UI-088). Colour mode
 * (by-alert-type / by-priority / single) + a single-colour swatch (shown in
 * ``single`` mode) + an **editable by-value legend** (shown in the two data-
 * driven modes; inc-items 4/5): one per-value colour picker per enum stop, each
 * defaulting to — and resettable back to — the design-system token the Map paints
 * (``getAlertTypeColor`` / ``getAlertPriorityColor``), bound to the matching
 * ``map.alert.type_colors`` / ``priority_colors`` override map (REQ-UI-046). A
 * glyph picker writes ``map.alert.glyph`` (REQ-UI-088).
 */
function AlertControls({
  alert,
  onChange,
}: {
  alert: AlertMapSettings;
  onChange: (alert: AlertMapSettings) => void;
}): React.ReactElement {
  return (
    <SubGroup label="Alert">
      <ColorGroup label="Marker colour" headerId="profile-editor-alert-color-label" flush>
        <ColorModeRadio
          name="profile-editor-alert-color-mode"
          value={alert.color_mode}
          options={[
            { value: "by_alert_type", label: "By alert type" },
            { value: "by_priority", label: "By priority" },
            { value: "single", label: "Single colour" },
          ]}
          onChange={(mode) =>
            onChange({ ...alert, color_mode: mode as AlertMapSettings["color_mode"] })
          }
          testIdPrefix="profile-editor-alert-color"
        />
        {alert.color_mode === "single" && (
          <ColorSwatch
            label="Marker colour"
            hideLabel
            value={alert.single_color}
            testId="profile-editor-alert-single-color"
            onChange={(single_color) => onChange({ ...alert, single_color })}
          />
        )}
        {alert.color_mode === "by_alert_type" && (
          <ByValueLegend
            testId="profile-editor-alert-legend"
            entries={ALERT_TYPE_LEGEND_ENTRIES}
            overrides={alert.type_colors}
            onChange={(type_colors) => onChange({ ...alert, type_colors })}
          />
        )}
        {alert.color_mode === "by_priority" && (
          <ByValueLegend
            testId="profile-editor-alert-legend"
            entries={ALERT_PRIORITY_LEGEND_ENTRIES}
            overrides={alert.priority_colors}
            onChange={(priority_colors) => onChange({ ...alert, priority_colors })}
          />
        )}
      </ColorGroup>
      <GlyphPicker
        label="Glyph"
        value={alert.glyph}
        testIdPrefix="profile-editor-alert-glyph"
        onChange={(glyph) => onChange({ ...alert, glyph })}
      />
    </SubGroup>
  );
}

/**
 * The platform **By-Status** legend (inc-items 4/5; REQ-UI-086 / REQ-UI-046) —
 * the missing platform counterpart to the alert legends. Renders an editable
 * colour picker per :type:`MarkerStatus` value (``healthy`` / ``degraded`` /
 * ``unhealthy`` / ``unknown`` / ``stale``), each defaulting to the live
 * ``--status-*`` token the snapshot layer paints (:func:`getStatusColor`),
 * bound to the ``map.platform.status_colors`` override map. Shown only in
 * ``by_status`` mode.
 */
function PlatformStatusLegend({
  overrides,
  onChange,
}: {
  overrides: OverrideColors | undefined;
  onChange: (overrides: OverrideColors) => void;
}): React.ReactElement {
  return (
    <ByValueLegend
      testId="profile-editor-platform-legend"
      entries={PLATFORM_STATUS_LEGEND_ENTRIES}
      overrides={overrides}
      onChange={onChange}
    />
  );
}

/** One by-value legend row spec — its stable override key, operator label, and a
 *  thunk resolving the live design-system token default (read lazily so a theme
 *  flip yields the current token; inc-items 4/5). */
interface LegendEntry {
  /** Stable override-map key (identical between writer + render reader). */
  key: string;
  /** Operator-facing value name — the picker's accessible label (REQ-UI-094). */
  label: string;
  /** Resolve the live design-system token default (the picker's value when the
   *  key is absent from the override map). */
  resolveDefault: () => string;
}

/** Platform status legend rows (REQ-UI-086). Keys are the :type:`MarkerStatus`
 *  ids; defaults resolve the live ``--status-*`` token. ``unhealthy`` shows the
 *  storyboard operator term "Offline" (design-system §"Semantic / status"). */
const PLATFORM_STATUS_LEGEND_ENTRIES: readonly LegendEntry[] = (
  [
    ["healthy", "Healthy"],
    ["degraded", "Degraded"],
    ["unhealthy", "Offline"],
    ["unknown", "Unknown"],
    ["stale", "Stale"],
  ] as const satisfies readonly (readonly [MarkerStatus, string])[]
).map(([status, label]) => ({
  key: status,
  label,
  resolveDefault: () => getStatusColor(status),
}));

/** Alert-type legend rows (REQ-UI-088). Keys are the stable enum-name keys
 *  (:const:`ALERT_TYPE_OVERRIDE_KEY`); defaults resolve the live
 *  ``--alert-type-*`` token; labels from :func:`alertTypeLabel`. ``UNSPECIFIED``
 *  is omitted (not an operator override target). */
const ALERT_TYPE_LEGEND_ENTRIES: readonly LegendEntry[] = [
  ALERT_TYPE.INFORMATION,
  ALERT_TYPE.WARNING,
  ALERT_TYPE.CRITICAL,
  ALERT_TYPE.ERROR,
  ALERT_TYPE.FATAL,
  ALERT_TYPE.MODE_CHANGE,
].map((t) => ({
  key: ALERT_TYPE_OVERRIDE_KEY[t],
  label: alertTypeLabel(t),
  resolveDefault: () => getAlertTypeColor(t),
}));

/** Alert-priority legend rows (REQ-UI-088). Keys are the stable enum-name keys
 *  (:const:`ALERT_PRIORITY_OVERRIDE_KEY`); defaults resolve the live
 *  ``--alert-priority-*`` token; labels from :func:`describeAlertPriority`. */
const ALERT_PRIORITY_LEGEND_ENTRIES: readonly LegendEntry[] = [
  ALERT_PRIORITY.LOW,
  ALERT_PRIORITY.MEDIUM,
  ALERT_PRIORITY.HIGH,
  ALERT_PRIORITY.CRITICAL,
].map((p) => ({
  key: ALERT_PRIORITY_OVERRIDE_KEY[p],
  label: describeAlertPriority(p).label,
  resolveDefault: () => getAlertPriorityColor(p),
}));

/**
 * An **editable by-value colour legend** (inc-items 4/5; REQ-UI-086/088 /
 * REQ-UI-094) — one labelled colour picker per value, each bound to that value's
 * entry in a sparse override map. Used by the platform By-Status legend and both
 * alert by-value legends, so the picker + reset UX is written once (CLAUDE.md
 * §4.4).
 *
 * Per row (:func:`LegendColorRow`):
 *   - the picker's current value is ``override[key] ?? entry.resolveDefault()``
 *     — the live design-system token when the value is not overridden, read via
 *     the running :func:`getComputedStyle` resolver so a theme flip still shows
 *     the right default;
 *   - changing the picker writes ``override[key] = "#RRGGBB"`` (the map stays
 *     sparse — a value left at its token has no key);
 *   - a per-row **Reset** affordance (shown only when the key is present) deletes
 *     that key so the value falls back to the token.
 *
 * The map is kept sparse so an un-overridden value always tracks the live theme
 * token (never a frozen snapshot of the resolved hex). The value name is the
 * row's accessible label (REQ-UI-094).
 */
function ByValueLegend({
  testId,
  entries,
  overrides,
  onChange,
}: {
  testId: string;
  entries: readonly LegendEntry[];
  overrides: OverrideColors | undefined;
  onChange: (overrides: OverrideColors) => void;
}): React.ReactElement {
  const setOverride = (key: string, hex: string): void => {
    onChange({ ...overrides, [key]: hex });
  };
  const resetOverride = (key: string): void => {
    const next: Record<string, string> = { ...overrides };
    delete next[key];
    onChange(next);
  };
  return (
    <div className="mt-2" data-testid={testId}>
      <span className="text-meta text-text-secondary">Legend</span>
      <ul className="mt-1 flex flex-col gap-1.5">
        {entries.map((entry) => (
          <LegendColorRow
            key={entry.key}
            entry={entry}
            override={overrides?.[entry.key]}
            testIdPrefix={`${testId}-${entry.key}`}
            onChange={(hex) => setOverride(entry.key, hex)}
            onReset={() => resetOverride(entry.key)}
          />
        ))}
      </ul>
    </div>
  );
}

/**
 * One editable legend row (inc-items 4/5; REQ-UI-094) — a native
 * ``<input type="color">`` swatch (the SAME control "Single colour" uses) +
 * the value label + a per-row Reset shown only when the value is overridden.
 *
 * The swatch value resolves to the override hex when present, else the live
 * design-system token (``entry.resolveDefault()`` — re-read each render so a
 * theme flip updates an un-overridden row's shown default). The colour input is
 * a properly labelled control: its ``aria-label`` is the value name.
 */
function LegendColorRow({
  entry,
  override,
  testIdPrefix,
  onChange,
  onReset,
}: {
  entry: LegendEntry;
  override: string | undefined;
  testIdPrefix: string;
  onChange: (hex: string) => void;
  onReset: () => void;
}): React.ReactElement {
  const isOverridden = typeof override === "string" && override.trim() !== "";
  // The native colour input requires a concrete ``#RRGGBB`` value; when not
  // overridden, seed it with the live token so the swatch shows the painted
  // default (read each render → theme-flip-aware).
  const value = isOverridden ? override : entry.resolveDefault();
  return (
    <li className="flex items-center gap-2 text-meta text-text-primary">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testIdPrefix}
        aria-label={entry.label}
        className="h-5 w-8 shrink-0 cursor-pointer rounded border border-border bg-bg-surface"
      />
      <span className="flex-1">{entry.label}</span>
      {isOverridden && (
        <button
          type="button"
          onClick={onReset}
          data-testid={`${testIdPrefix}-reset`}
          aria-label={`Reset ${entry.label} colour to default`}
          title="Reset to default"
          className={cn(
            "shrink-0 rounded border border-border px-1.5 py-0.5 text-meta text-text-secondary",
            "hover:bg-bg-elevated hover:text-text-primary",
          )}
        >
          Reset
        </button>
      )}
    </li>
  );
}

// ════════════════════════════════════════════════════════════════════
// Entity-Details editor section (REQ-UI-092)
// ════════════════════════════════════════════════════════════════════

/** The Profile ``entity_details`` sub-document (ADR-033 §10). */
type EntityDetailsDefaults = components["schemas"]["EntityDetailsDefaults"];
/** One persisted section default (``{ section_id, visible, expanded }``). */
type SectionDefault = components["schemas"]["SectionDefault"];

/**
 * The eight entity types, in the order the editor lists their groups: the four
 * map/list kinds first, then the object-track kind (ADR-041), then the System
 * panel's three status selection kinds
 * (ADR-024 §4 amended 2026-07-31; REQ-UI-131).
 *
 * The status kinds are listed for the same reason as the others — this editor
 * sets the Profile **defaults** a freshly-loaded panel starts from. Their live
 * collapse is still session-local: an operator's triage toggle never writes
 * here, it is captured only when settings are saved as a Profile.
 */
const ENTITY_TYPES: readonly EntityDetailType[] = [
  "platform",
  "aggregator",
  "detection",
  "alert",
  "track",
  "status_component",
  "status_interface",
  "status_connection",
];

/** Operator-facing titles for the per-type sub-groups. The status labels use
 *  the panel's operator vocabulary (component / interface / connection on the
 *  System display), not the wire key. */
const ENTITY_TYPE_LABELS: Record<EntityDetailType, string> = {
  platform: "Platform",
  aggregator: "Aggregator",
  detection: "Detection",
  alert: "Alert",
  track: "Track",
  status_component: "System component",
  status_interface: "System interface",
  status_connection: "System connection",
};

/**
 * The Entity-Details editor section (ADR-033 §10; REQ-UI-092). Complements the
 * in-panel gear from increment 7: where that toggles the *live* panel, this
 * edits the Profile **defaults** applied to a fresh panel.
 *
 * A collapsible group with a per-type list of that type's canonical sections —
 * each row a **visible** toggle + a **default-expanded** toggle — writing
 * ``{ section_id, visible, expanded }`` into ``entity_details.<type>.sections``,
 * for all seven types (the four map/list kinds plus the three System selection
 * kinds; see :const:`ENTITY_TYPES`). The canonical ids + titles are reused from
 * :file:`entity-details/use-section-visibility.ts` rather than redefined, so the
 * editor and the live panels stay in lock-step. An id absent from the persisted
 * document defaults to visible + expanded (REQ-UI-092).
 *
 * Each edit computes the whole next ``entity_details`` document (preserving the
 * other types) and hands it to ``onChange`` (wired to
 * ``setWorkingState({ entity_details })`` — the entity-detail panels re-render
 * off the live working state).
 *
 * @param entityDetails - The current ``workingState.entity_details``.
 * @param onChange - Receives the whole next ``entity_details`` document.
 */
function EntityDetailsSection({
  entityDetails,
  onChange,
}: {
  entityDetails: EntityDetailsDefaults | undefined;
  onChange: (entityDetails: EntityDetailsDefaults) => void;
}): React.ReactElement {
  const setSections = (type: EntityDetailType, sections: SectionDefault[]): void => {
    onChange({ ...entityDetails, [type]: { ...entityDetails?.[type], sections } });
  };

  return (
    <ProfileEditorSection title="Selection Details" testId="entity-details">
      <p className="mb-2 text-meta text-text-muted">
        Default visibility and expand state for each entity type&apos;s detail sections.
      </p>
      <div className="flex flex-col gap-3">
        {ENTITY_TYPES.map((type) => (
          <EntityTypeSectionList
            key={type}
            type={type}
            sections={extractTypeSections(entityDetails, type)}
            onChange={(next) => setSections(type, next)}
          />
        ))}
      </div>
    </ProfileEditorSection>
  );
}

/**
 * The per-type section list — one row per canonical section id of ``type``,
 * each with a **visible** + a **default-expanded** toggle. The persisted
 * ``sections`` array supplies overrides; any canonical id it omits renders at
 * the visible+expanded default (REQ-UI-092). A toggle upserts that id's default
 * into the array (patching only the changed dimension; the other defaults to
 * ``true`` for a brand-new entry).
 */
function EntityTypeSectionList({
  type,
  sections,
  onChange,
}: {
  type: EntityDetailType;
  sections: readonly SectionDefault[];
  onChange: (sections: SectionDefault[]) => void;
}): React.ReactElement {
  const byId = new Map<string, SectionDefault>();
  for (const s of sections) {
    byId.set(s.section_id, s);
  }
  const titles: Record<string, string> = SECTION_TITLES[type];

  const upsert = (
    sectionId: string,
    patch: Partial<Pick<SectionDefault, "visible" | "expanded">>,
  ): void => {
    onChange(upsertSection(sections, sectionId, patch));
  };

  return (
    <SubGroup label={ENTITY_TYPE_LABELS[type]}>
      <div className="flex flex-col gap-1.5" data-testid={`profile-editor-entity-${type}`}>
        {SECTION_IDS[type].map((sectionId) => {
          const entry = byId.get(sectionId);
          const visible = entry?.visible ?? true;
          const expanded = entry?.expanded ?? true;
          const label = titles[sectionId] ?? sectionId;
          return (
            <div
              key={sectionId}
              data-testid={`profile-editor-entity-${type}-${sectionId}`}
              className="flex items-center justify-between gap-3"
            >
              <span className="text-body-sm text-text-primary">{label}</span>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1 text-body-sm text-text-secondary">
                  <input
                    type="checkbox"
                    checked={visible}
                    onChange={(e) => upsert(sectionId, { visible: e.target.checked })}
                    data-testid={`profile-editor-entity-${type}-${sectionId}-visible`}
                    aria-label={`${label} visible`}
                  />
                  Visible
                </label>
                <label className="flex items-center gap-1 text-body-sm text-text-secondary">
                  <input
                    type="checkbox"
                    checked={expanded}
                    onChange={(e) => upsert(sectionId, { expanded: e.target.checked })}
                    data-testid={`profile-editor-entity-${type}-${sectionId}-expanded`}
                    aria-label={`${label} expanded by default`}
                  />
                  Expanded
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </SubGroup>
  );
}

// ════════════════════════════════════════════════════════════════════
// Small presentational helpers
// ════════════════════════════════════════════════════════════════════

/**
 * A labelled sub-group within a section — a subsection **header** (a small
 * uppercase ``text-meta`` label, one tier below the section's 12 px
 * ``text-body-sm`` header so the hierarchy reads SECTION › SUBSECTION at a
 * glance) over its controls, separated from the preceding subsection by a top
 * rule + extra top spacing (item 6 — the Map section's Platform / Detection /
 * Alert subsections must not blur together).
 *
 * The rule + top spacing are intrinsic and reset on the **first** subsection
 * (``first:border-t-0 first:pt-0``) so the leading subsection sits flush under
 * the section header rather than carrying a redundant divider. Every group that
 * stacks subsections — Map (Tile / Layer visibility / Platform / Detection /
 * Alert) and Entity Details (Platform / Aggregator / Detection / Alert) — renders
 * its ``SubGroup``s as siblings, so ``first:`` targets the leading one in each.
 * The label is purely presentational (the controls below carry their own
 * accessible labels), so it stays a ``<span>`` rather than a heading — no extra
 * landmark noise (REQ-UI-094).
 */
function SubGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="border-t border-border pt-3 first:border-t-0 first:pt-0">
      <span className="text-meta font-semibold uppercase tracking-wide text-text-muted">
        {label}
      </span>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

/** A single labelled checkbox row (label left, box right). */
function CheckboxRow({
  label,
  checked,
  testId,
  onChange,
}: {
  label: string;
  checked: boolean;
  testId: string;
  onChange: (checked: boolean) => void;
}): React.ReactElement {
  return (
    <label className="flex items-center gap-2 text-body-sm text-text-primary">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
      />
      {label}
    </label>
  );
}

/** A colour-mode radio group (used by Platform + Alert). */
function ColorModeRadio({
  name,
  value,
  options,
  onChange,
  testIdPrefix,
}: {
  name: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
  testIdPrefix: string;
}): React.ReactElement {
  return (
    <div className="flex flex-wrap gap-3">
      {options.map((opt) => (
        <label key={opt.value} className="flex items-center gap-1.5 text-body-sm text-text-primary">
          <input
            type="radio"
            name={name}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            data-testid={`${testIdPrefix}-${opt.value}`}
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

/**
 * A labelled **marker-colour group** (architect 2026-06-08 — formatting pass) —
 * a visible header (the SAME 12 px ``text-body-sm font-medium text-text-secondary``
 * treatment {@link GlyphPicker} gives its "Marker glyph" label) over the colour
 * controls (the {@link ColorModeRadio} toggle + the swatch / legend), so each
 * marker's colour cluster reads as a labelled section symmetric with its glyph
 * picker (REQ-UI-086/087/088). The structure mirrors GlyphPicker's
 * ``<div className="mt-3"><span …label…/><div className="mt-1">…controls…</div></div>``.
 *
 * Accessibility (REQ-UI-094): the controls sit in a ``role="group"`` linked to
 * the header via ``aria-labelledby`` (mirroring how GlyphPicker links its
 * radiogroup to ``${testIdPrefix}-label``), so the cluster announces under the
 * "Marker colour" name. The child controls keep their own native semantics —
 * the toggle's radios share a ``name``; the swatch/legend pickers keep their
 * per-value ``aria-label`` — so keyboard + radio behaviour is unchanged.
 *
 * @param label - The group's visible caption (and the controls' group label).
 * @param headerId - The header element id the group's ``aria-labelledby`` points
 *     at (per-call unique so multiple groups don't collide).
 * @param flush - Drop the top margin when this is the **first** group in its
 *     SubGroup (it then sits flush under the subsection header rather than
 *     carrying a redundant gap), keeping the spacing tidy.
 */
function ColorGroup({
  label,
  headerId,
  flush = false,
  children,
}: {
  label: string;
  headerId: string;
  flush?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={flush ? undefined : "mt-3"}>
      <span id={headerId} className="text-body-sm font-medium text-text-secondary">
        {label}
      </span>
      <div role="group" aria-labelledby={headerId} className="mt-1">
        {children}
      </div>
    </div>
  );
}

/** A labelled colour swatch (native ``<input type="color">``) — the
 *  single-colour picker for the platform / detection / alert markers
 *  (REQ-UI-086/087/088). The value is a literal hex the Profile stores
 *  verbatim (ADR-033 §9.2).
 *
 *  ``hideLabel`` suppresses the *visible* caption text while keeping the
 *  control's ``aria-label`` (a native ``<input type="color">`` must stay
 *  accessibly labelled — REQ-UI-094). Used inside a {@link ColorGroup}, whose
 *  "Marker colour" header already supplies the visible label, so the swatch
 *  text would otherwise duplicate it. */
function ColorSwatch({
  label,
  value,
  testId,
  onChange,
  hideLabel = false,
}: {
  label: string;
  value: string;
  testId: string;
  onChange: (value: string) => void;
  hideLabel?: boolean;
}): React.ReactElement {
  return (
    <label className="mt-2 flex items-center gap-2 text-body-sm text-text-primary">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        aria-label={label}
        className="h-6 w-9 cursor-pointer rounded border border-border bg-bg-surface"
      />
      {!hideLabel && label}
    </label>
  );
}

/**
 * The marker **glyph picker** (REQ-UI-086/087/088; REQ-UI-094) — a labelled row
 * of selectable shape swatches drawn from the closed
 * :data:`map/glyphs.GLYPH_IDS` vocabulary, writing one ``map.<type>.glyph`` id.
 *
 * Built as a native **radiogroup** (one ``<input type="radio">`` per swatch in a
 * shared ``name``) so it is keyboard-operable for free and announces as a
 * labelled radio group (REQ-UI-094). The radio is visually hidden (``sr-only``)
 * behind a {@link GlyphIcon} swatch; the selected swatch takes the action accent
 * so the current selection reads at a glance in both themes (REQ-UI-040). The
 * stored value is normalised through {@link normalizeGlyphId} so an unknown id
 * highlights the default circle rather than nothing (CLAUDE.md §8.1).
 *
 * @param label - The group's visible caption + ``aria-label``.
 * @param value - The current ``map.<type>.glyph`` id (possibly unknown/blank).
 * @param testIdPrefix - Per-call test-id root; each swatch is
 *     ``<prefix>-<glyphId>`` and the group is ``<prefix>-group``.
 * @param onChange - Receives the chosen {@link GlyphId}.
 */
function GlyphPicker({
  label,
  value,
  testIdPrefix,
  onChange,
}: {
  label: string;
  value: string;
  testIdPrefix: string;
  onChange: (glyph: GlyphId) => void;
}): React.ReactElement {
  const selected = normalizeGlyphId(value);
  const groupName = `${testIdPrefix}-radio`;
  return (
    <div className="mt-3">
      <span id={`${testIdPrefix}-label`} className="text-body-sm font-medium text-text-secondary">
        {label}
      </span>
      <div
        role="radiogroup"
        aria-labelledby={`${testIdPrefix}-label`}
        data-testid={`${testIdPrefix}-group`}
        className="mt-1 flex flex-wrap gap-1.5"
      >
        {GLYPH_IDS.map((glyph) => {
          const isSelected = glyph === selected;
          return (
            <label
              key={glyph}
              title={GLYPH_LABELS[glyph]}
              className={cn(
                "flex h-8 w-8 cursor-pointer items-center justify-center rounded border",
                isSelected
                  ? "border-brand bg-brand/10 text-brand"
                  : "border-border text-text-secondary hover:border-decor-muted hover:text-text-primary",
                "focus-within:outline focus-within:outline-2 focus-within:outline-brand",
              )}
            >
              <input
                type="radio"
                name={groupName}
                className="sr-only"
                checked={isSelected}
                aria-label={GLYPH_LABELS[glyph]}
                data-testid={`${testIdPrefix}-${glyph}`}
                onChange={() => onChange(glyph)}
              />
              <GlyphIcon glyph={glyph} size={16} />
            </label>
          );
        })}
      </div>
    </div>
  );
}

// Form controls read as **sunken inputs** against their section card: the
// section now renders on ``bg-bg-surface`` (item 7), so controls drop to
// ``bg-bg-base`` (the same darkest surface the Save-as-new name input already
// uses) to stay legible as distinct fields rather than blending into the card.
const inputClass =
  "rounded border border-border bg-bg-base px-2 py-1 text-body-sm text-text-primary focus:border-brand focus:outline-none disabled:cursor-not-allowed disabled:opacity-60";

const selectClass =
  "rounded border border-border bg-bg-base px-2 py-1.5 text-body-sm text-text-primary focus:border-brand focus:outline-none disabled:cursor-not-allowed disabled:opacity-60";

// ════════════════════════════════════════════════════════════════════
// Pure helpers
// ════════════════════════════════════════════════════════════════════

/** A fresh clock row default (ADR-033 §8 defaults). The label auto-fills from
 *  the default timezone so a new row is never unlabelled (REQ-UI-085). */
function defaultClock(): ClockSettings {
  return {
    label: clockLabelFromTimezone("UTC"),
    timezone: "UTC",
    hour_cycle: "h23",
    show_seconds: true,
    show_date: false,
  };
}

/**
 * Derive a short, human label from an IANA timezone — the city/zone name (the
 * segment after the last ``/``, underscores → spaces), or ``"UTC"`` for the
 * single-segment UTC zone. Clamped to {@link CLOCK_LABEL_MAX}.
 */
function clockLabelFromTimezone(timezone: string): string {
  const tail = timezone.split("/").at(-1) ?? timezone;
  return tail.replace(/_/g, " ").slice(0, CLOCK_LABEL_MAX);
}

/**
 * Make ``base`` unique against ``existing`` (case-insensitive) by appending
 * `` 2``, `` 3``, … until it no longer collides, keeping the result within
 * {@link CLOCK_LABEL_MAX}.
 */
function uniqueClockLabel(base: string, existing: readonly string[]): string {
  const taken = new Set(existing.map((l) => l.trim().toLowerCase()));
  if (!taken.has(base.trim().toLowerCase())) {
    return base;
  }
  for (let n = 2; ; n += 1) {
    const suffix = ` ${n}`;
    const candidate = base.slice(0, CLOCK_LABEL_MAX - suffix.length) + suffix;
    if (!taken.has(candidate.trim().toLowerCase())) {
      return candidate;
    }
  }
}

/**
 * The (case-insensitive) labels in ``clocks`` that collide with an earlier row.
 * Empty labels are ignored. Used to flag duplicate rows inline and to gate Add /
 * Save — labels (not timezones) must be unique (REQ-UI-085; ADR-033 §8).
 */
function duplicateClockLabelIndices(clocks: readonly ClockSettings[]): ReadonlySet<number> {
  const seen = new Map<string, number>();
  const dupes = new Set<number>();
  clocks.forEach((clock, index) => {
    const key = (clock.label ?? "").trim().toLowerCase();
    if (key === "") {
      return;
    }
    if (seen.has(key)) {
      dupes.add(index);
    } else {
      seen.set(key, index);
    }
  });
  return dupes;
}

/** Clamp a candidate width to the drawer's resize bounds. */
function clampWidth(value: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, value));
}

/**
 * A small bundled timezone set used **only** when the runtime cannot enumerate
 * IANA zones (jsdom under Vitest; the rare browser without
 * ``Intl.supportedValuesOf``). The real browser gets the full ≈400-zone list.
 */
const FALLBACK_TIMEZONES: readonly string[] = [
  "UTC",
  "America/Denver",
  "America/New_York",
  "America/Los_Angeles",
  "America/Chicago",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Australia/Sydney",
];

/**
 * The IANA timezones the runtime exposes, plus "UTC" pinned first.
 * ``Intl.supportedValuesOf`` returns the full list in every modern browser; we
 * fall back to {@link FALLBACK_TIMEZONES} only when that API is genuinely
 * unavailable. The result is the closed list the constrained picker commits
 * from — an invalid free-text zone can never reach ``Intl.DateTimeFormat``.
 */
function supportedTimezones(): readonly string[] {
  try {
    if (typeof Intl.supportedValuesOf !== "function") {
      return FALLBACK_TIMEZONES;
    }
    const supported = Intl.supportedValuesOf("timeZone");
    return ["UTC", ...supported.filter((tz) => tz !== "UTC")];
  } catch {
    return FALLBACK_TIMEZONES;
  }
}

// ---- Entity-details helpers (REQ-UI-092) -----------------------------

/**
 * The persisted ``sections`` array for one entity type, filtered to well-formed
 * entries. Mirrors the read discipline in
 * :file:`entity-details/use-section-visibility.ts` so a malformed document never
 * injects a bad row into the editor (CLAUDE.md §8.1).
 */
function extractTypeSections(
  doc: EntityDetailsDefaults | undefined,
  type: EntityDetailType,
): SectionDefault[] {
  const sections = doc?.[type]?.sections;
  if (!Array.isArray(sections)) {
    return [];
  }
  return sections.filter(isSectionEntry);
}

/** Type-guard for a well-formed ``{ section_id, visible, expanded }`` row. */
function isSectionEntry(value: unknown): value is SectionDefault {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.section_id === "string" &&
    typeof obj.visible === "boolean" &&
    typeof obj.expanded === "boolean"
  );
}

/**
 * Upsert one section default, patching only the supplied dimension and
 * preserving the other. A brand-new entry defaults its unpatched dimension to
 * ``true`` (the "show + expand by default" semantics per ADR-033 §10 /
 * REQ-UI-092) — the same contract the live panel's
 * ``useSectionVisibility.upsertSectionField`` uses, kept consistent so the
 * editor's defaults and the panel's toggles agree.
 */
function upsertSection(
  current: readonly SectionDefault[],
  sectionId: string,
  patch: Partial<Pick<SectionDefault, "visible" | "expanded">>,
): SectionDefault[] {
  const index = current.findIndex((s) => s.section_id === sectionId);
  if (index >= 0) {
    const next = current.slice();
    next[index] = { ...current[index], ...patch };
    return next;
  }
  return [...current, { section_id: sectionId, visible: true, expanded: true, ...patch }];
}
